import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
        <link rel="canonical" href="https://www.lingu-pix.app/terms-and-conditions" />
        <title>Terms and Conditions – LinguPix</title>
        <meta name="description" content="Terms and Conditions for the LinguPix language learning application and website." />

      {/* Header */}
      <header className="bg-[#333333] text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center text-[#FFA55A] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold">Terms and Conditions</h1>
          <p className="text-gray-300 mt-2">Last updated: March 27, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1">
        <div className="prose prose-lg max-w-none">
                    {/* 1. About These Terms */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">1. About These Terms</h2>
            <p className="text-[#666666] leading-relaxed mb-6">These Terms and Conditions ("Terms") govern your access to and use of:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>the LinguPix mobile application for iOS and Android;</li>
              <li>the LinguPix website available at <code className="bg-gray-100 px-1 py-0.5 rounded text-sm text-[#FF7F30]">www.lingu-pix.app</code>;</li>
              <li>public account-management pages such as password reset and account deletion; and</li>
              <li>related support, subscription, and account services.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">By creating an account, using LinguPix, purchasing a subscription, or using the website, you agree to these Terms.</p>
            <p className="text-[#666666] leading-relaxed mb-6">If you do not agree to these Terms, do not use the Service.</p>
          </section>

          {/* 2. Who We Are */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">2. Who We Are</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix ("LinguPix", "we", "us", "our") provides a language-learning service for children that is set up and managed by an adult account holder, such as a parent, guardian, tutor, educator, or school representative.</p>
            <p className="text-[#666666] leading-relaxed mb-6">For legal, privacy, and account matters, contact:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:contact@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">contact@lingu-pix.app</a></li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">For product support, contact:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:support@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">support@lingu-pix.app</a></li>
            </ul>
          </section>

          {/* 3. Who May Use LinguPix */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">3. Who May Use LinguPix</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix is intended to be created and managed by an adult.</p>
            <p className="text-[#666666] leading-relaxed mb-6">By using the Service, you confirm that:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>you are at least 18 years old; or</li>
              <li>you are otherwise legally authorized to create and manage the account and any child profiles connected to it.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">Children may use the learning experience only under the supervision, authority, or responsibility of the adult account holder.</p>
          </section>

          {/* 4. What LinguPix Provides */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">4. What LinguPix Provides</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix is a digital language-learning service for children. Depending on the plan and platform, the Service may include:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>child learning profiles;</li>
              <li>image-based vocabulary practice;</li>
              <li>swipe-based review and repetition;</li>
              <li>audio playback and narrator voice options;</li>
              <li>progress tracking, rewards, badges, and streaks;</li>
              <li>reminder notifications;</li>
              <li>subscription-based premium access;</li>
              <li>plan-based device limits or learner slots; and</li>
              <li>account support, password reset, and account deletion flows.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">We may improve, update, replace, or remove features over time.</p>
          </section>

          {/* 5. Accounts and Child Profiles */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">5. Accounts and Child Profiles</h2>
            <p className="text-[#666666] leading-relaxed mb-6">To use certain parts of the Service, you may need to create an account.</p>
            <p className="text-[#666666] leading-relaxed mb-6">You agree to:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>provide accurate, current, and complete information;</li>
              <li>keep your login credentials secure;</li>
              <li>use the Service only for lawful and authorized purposes;</li>
              <li>take responsibility for activity that happens through your account.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">The adult account holder is responsible for any child profile created in LinguPix, including the name, avatar, selected language, settings, and use of the Service.</p>
            <p className="text-[#666666] leading-relaxed mb-6">You should only create child profiles for children you are authorized to manage.</p>
          </section>

          {/* 6. Acceptable Use */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">6. Acceptable Use</h2>
            <p className="text-[#666666] leading-relaxed mb-6">You may use LinguPix only in a way that is lawful, fair, and consistent with these Terms.</p>
            <p className="text-[#666666] leading-relaxed mb-6">You must not:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>impersonate another person or create unauthorized accounts;</li>
              <li>interfere with the app, website, security, or other users;</li>
              <li>misuse support channels, account systems, or public account-management pages;</li>
              <li>attempt to bypass subscription, licensing, or device-limit controls;</li>
              <li>copy, reverse engineer, or exploit the Service beyond what is permitted by law;</li>
              <li>upload or send unlawful, abusive, harmful, or infringing content.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">We may suspend or restrict access if we reasonably believe the Service is being misused.</p>
          </section>

          {/* 7. Plans, Subscriptions, and Paid Features */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">7. Plans, Subscriptions, and Paid Features</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Some features of LinguPix may require a paid subscription.</p>
            <p className="text-[#666666] leading-relaxed mb-6">Plan types, billing periods, device limits, learner-slot limits, trial eligibility, promotional offers, and exact prices are shown in the App Store or Google Play at the time of purchase.</p>
            <p className="text-[#666666] leading-relaxed mb-6">If you buy a subscription:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>billing is handled by Apple App Store or Google Play, depending on where you purchased;</li>
              <li>your subscription may automatically renew unless you cancel it before renewal;</li>
              <li>the relevant store controls billing, renewals, taxes, receipts, and store-side subscription management;</li>
              <li>LinguPix may use RevenueCat to validate entitlement status and manage access, but RevenueCat is not the merchant of record and does not replace Apple or Google Play billing rules.</li>
            </ul>
          </section>

          {/* 8. Managing, Cancelling, and Restoring Subscriptions */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">8. Managing, Cancelling, and Restoring Subscriptions</h2>
            <p className="text-[#666666] leading-relaxed mb-6">If your subscription was purchased through Apple App Store or Google Play:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>you can manage, upgrade, downgrade, or cancel it through the relevant store account;</li>
              <li>restoring purchases is available inside the app for eligible store purchases;</li>
              <li>cancellation stops future renewals, but access may continue until the end of the current paid billing period, subject to the applicable store rules and law.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">Deleting your LinguPix account does <strong>not</strong> automatically cancel an active subscription. If you want to stop future billing, you must also cancel the subscription through the App Store or Google Play.</p>
          </section>

          {/* 9. Refunds */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">9. Refunds</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Refunds for in-app purchases and subscriptions are governed primarily by the policies and tools of the store through which you purchased the subscription.</p>
            <p className="text-[#666666] leading-relaxed mb-6">In practical terms:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>if you purchased through <strong>Apple App Store</strong>, refund requests should be submitted through Apple's refund flow and policies;</li>
              <li>if you purchased through <strong>Google Play</strong>, refund requests are subject to Google Play policies, store tools, and applicable law;</li>
              <li>refund rights may vary by store, country, payment method, local law, and purchase type.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix does not process your card payments directly and does not act as the payment processor for App Store or Google Play purchases. RevenueCat does not decide or issue refunds for those purchases.</p>
            <p className="text-[#666666] leading-relaxed mb-6">Where legally permitted, we may provide support or case-by-case assistance, but we do not guarantee discretionary refunds outside the rights and processes provided by the applicable store or law.</p>
          </section>

          {/* 10. Free Plans, Trials, and Offers */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">10. Free Plans, Trials, and Offers</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix may offer free access, limited plans, introductory offers, or free trials.</p>
            <p className="text-[#666666] leading-relaxed mb-6">Unless expressly stated otherwise:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>free plans and free features may change over time;</li>
              <li>trial or offer eligibility may depend on the store, region, or account history;</li>
              <li>promotional offers may be changed or withdrawn where legally permitted.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">If a free trial or introductory offer is shown in the App Store or Google Play, the store listing and purchase flow govern the exact terms of that offer.</p>
          </section>

          {/* 11. Device Limits and Licensing */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">11. Device Limits and Licensing</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Some plans include limits on the number of devices, learner slots, or both.</p>
            <p className="text-[#666666] leading-relaxed mb-6">You agree not to bypass or manipulate these limits. We may enforce plan limits automatically, including by restricting additional devices, blocking new activations, or limiting access outside the allowed plan scope.</p>
          </section>

          {/* 12. Notifications and Communications */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">12. Notifications and Communications</h2>
            <p className="text-[#666666] leading-relaxed mb-6">If you enable notifications, you agree that LinguPix may send service-related notifications such as reminders, streak nudges, badge or progress alerts, and other product messages tied to enabled features.</p>
            <p className="text-[#666666] leading-relaxed mb-6">We may also send transactional emails related to:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>account verification;</li>
              <li>password reset;</li>
              <li>subscription or access status;</li>
              <li>support and service issues;</li>
              <li>account deletion and legal notices.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">Where required by law, marketing communications will be handled separately from core service messages.</p>
          </section>

          {/* 13. Account Deletion */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">13. Account Deletion</h2>
            <p className="text-[#666666] leading-relaxed mb-6">You may request account deletion:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>inside the app; or</li>
              <li>through the public account deletion page at <Link to="/delete-account" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">www.lingu-pix.app/delete-account</Link></li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">The public web flow may require email verification before a deletion request is scheduled.</p>
            <p className="text-[#666666] leading-relaxed mb-6">After confirmation, the account may be blocked immediately and then enter a grace period before permanent deletion. Some limited data may be retained where required by law, for accounting, fraud prevention, security, or dispute resolution.</p>
            <p className="text-[#666666] leading-relaxed mb-6">Deleting your account does not itself cancel an active App Store or Google Play subscription.</p>
          </section>

          {/* 14. Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">14. Intellectual Property</h2>
            <p className="text-[#666666] leading-relaxed mb-6">The Service, including the app, website, branding, content structure, graphics, software, and other materials provided by LinguPix, is protected by intellectual property laws and belongs to us or our licensors.</p>
            <p className="text-[#666666] leading-relaxed mb-6">Except where allowed by law or expressly permitted by us, you may not copy, modify, distribute, sell, license, or create derivative works from the Service.</p>
          </section>

          {/* 15. Feedback */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">15. Feedback</h2>
            <p className="text-[#666666] leading-relaxed mb-6">If you send us feedback, ideas, or suggestions about LinguPix, you allow us to use that feedback to improve the Service without any obligation to compensate you.</p>
          </section>

          {/* 16. Third-Party Services and Platforms */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">16. Third-Party Services and Platforms</h2>
            <p className="text-[#666666] leading-relaxed mb-6">LinguPix may rely on third-party providers and platforms such as Apple, Google Play, Supabase, RevenueCat, Loops, Expo, and website hosting or analytics providers.</p>
            <p className="text-[#666666] leading-relaxed mb-6">Your use of those third-party platforms may also be subject to their own terms and policies. We are not responsible for third-party services that we do not control.</p>
          </section>

          {/* 17. Availability and Changes to the Service */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">17. Availability and Changes to the Service</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We may update, improve, suspend, or discontinue parts of the Service from time to time.</p>
            <p className="text-[#666666] leading-relaxed mb-6">We do not guarantee that the Service will always be available, uninterrupted, or error-free, although we aim to provide a reliable experience and fix issues as they arise.</p>
          </section>

          {/* 18. Suspension and Termination */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">18. Suspension and Termination</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We may suspend, restrict, or terminate access to the Service if:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>you materially breach these Terms;</li>
              <li>we reasonably believe your account is being used unlawfully, fraudulently, or abusively;</li>
              <li>we are required to do so for legal, regulatory, or security reasons.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">You may stop using the Service at any time. If you want your data removed, use the in-app or web account deletion flow.</p>
          </section>

          {/* 19. Consumer Rights, Disclaimers, and Liability */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">19. Consumer Rights, Disclaimers, and Liability</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Nothing in these Terms limits rights that cannot be excluded under applicable consumer law.</p>
            <p className="text-[#666666] leading-relaxed mb-6">To the fullest extent permitted by law:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li>the Service is provided on an "as available" basis;</li>
              <li>we do not guarantee uninterrupted availability or perfect compatibility with every device;</li>
              <li>we are not liable for indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mb-6">Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited under applicable law.</p>
          </section>

          {/* 20. Privacy */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">20. Privacy</h2>
            <p className="text-[#666666] leading-relaxed mb-6">Your use of the Service is also governed by our Privacy Policy:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><Link to="/privacy-policy" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">www.lingu-pix.app/privacy-policy</Link></li>
            </ul>
          </section>

          {/* 21. Governing Law */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">21. Governing Law</h2>
            <p className="text-[#666666] leading-relaxed mb-6">These Terms are governed by the laws of Poland, except where mandatory consumer protection law in your country of residence applies.</p>
          </section>

          {/* 22. Changes to These Terms */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">22. Changes to These Terms</h2>
            <p className="text-[#666666] leading-relaxed mb-6">We may update these Terms from time to time.</p>
            <p className="text-[#666666] leading-relaxed mb-6">If we make material changes, we may provide notice through the website, the app, email, or another appropriate channel. The updated version becomes effective from the date shown at the top of the Terms, unless stated otherwise.</p>
          </section>

          {/* 23. Contact */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-6">23. Contact</h2>
            <p className="text-[#666666] leading-relaxed mb-6">If you have questions about these Terms, contact:</p>
            <ul className="list-disc pl-6 space-y-3 text-[#666666] mb-6">
              <li><a href="mailto:contact@lingu-pix.app" className="text-[#FF7F30] hover:text-[#FFA55A] font-semibold underline">contact@lingu-pix.app</a></li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;